import styles from './Producao.module.css'
import { IconButton, Divider, Button, TextField, CircularProgress } from "@mui/material"
import { useEffect, useRef, useState } from "react"

import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import attendanceApi from '@/services/api/attendance'
import { delay } from '@/utils/delay'
import { formataDataEmDDMMYYYYHM } from '@/utils/formatDateTo'
import { CustomSnackbar, severityType } from '@/utils/alert'
import { useClient } from '@/contexts/ClientContext'
import { buscarArquivoProjetoTecnico, uploadProjetoTecnico } from '@/services/api/component'

interface ProducaoProps {
    idAtendimento: number
}

export function Producao({ idAtendimento }: ProducaoProps) {
    const [showAlert, setShowAlert] = useState(false);
    const [alertInfo, setAlertInfo] = useState<{
        severity: severityType
        message: string
    }>({ severity: 'success', message: '' })


    const [estado, setEstado] = useState('')
    const [medicao, setMedicao] = useState({
        dataEnvio: '',
        mensagem: '',
        atualizadoEm: ''
    })

    const [projetoTecnico, setProjetoTecnico] = useState<{
        dataAprovacao: string;
        mensagem: string;
        arquivo: File | null;
        atualizadoEm: string;
    }>({
        dataAprovacao: '',
        mensagem: '',
        arquivo: null,
        atualizadoEm: ''
    });    

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [arquivoSalvo, setArquivoSalvo] = useState<string | null>(null);
    
    const [producao, setProducao] = useState({
        dataEnvio: '',
        mensagem: '',
        atualizadoEm: ''
    })
    const [errorsMedicao, setErrorsMedicao] = useState({ data: false, mensagem: false });
    const [errorsProducao, setErrorsProducao] = useState({ data: false, mensagem: false });
    const [loading, setLoading] = useState({ medicao: false, producao: false, projeto: false });
    const [loadingProjeto, setLoadingProjeto] = useState(false);
    const [expanded, setExpanded] = useState({ geral: false, medicao: false, producao: false, projeto: false });
    const [atualizarEstado, setAtualizarEstado] = useState('')
    const clientContext = useClient()


    function handleInputChangeMedicao(e: React.ChangeEvent<HTMLInputElement>) {
        const { name, value } = e.target;
        setMedicao((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
    };

    async function buscarArquivoExistente() {
        try {
            const resposta = await buscarArquivoProjetoTecnico(idAtendimento);
            if (resposta.data && resposta.data.length > 0) {
                setArquivoSalvo(resposta.data[0].nome);
            }
        } catch (error) {
            console.error("Erro ao buscar arquivo existente", error);
        }
    }    

    function handleInputChangeProjeto(e: React.ChangeEvent<HTMLInputElement>) {
        const { name, value } = e.target;
        setProjetoTecnico((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
    }
    
    function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
        if (e.target.files && e.target.files[0]) {
            setProjetoTecnico((prevValues) => ({
                ...prevValues,
                arquivo: e.target.files ? e.target.files[0] : null,
            }));
        }
    }
    
    async function uploadProjeto() {
        if (arquivoSalvo && !projetoTecnico.arquivo) {
            await enviarMensagemParaCliente('Projeto Técnico');
            setAlertInfo({ severity: 'success', message: 'Projeto enviado com sucesso!' });
            setShowAlert(true);
            return;
        }

        if (!projetoTecnico.arquivo) {
            setAlertInfo({ severity: 'error', message: 'Selecione um arquivo!' });
            setShowAlert(true);
            return;
        }
        setLoadingProjeto(true);
    
        const formData = new FormData();
        formData.append('file', projetoTecnico.arquivo);
    
        try {
            await uploadProjetoTecnico(idAtendimento, formData);
            await enviarMensagemParaCliente('Projeto Técnico');
            setAlertInfo({ severity: 'success', message: 'Projeto enviado com sucesso!' });
        } catch (error) {
            setAlertInfo({ severity: 'error', message: 'Erro ao enviar projeto.' });
        } finally {
            setLoadingProjeto(false);
        }
        setShowAlert(true);
    }    

    function aprovarProjeto() {
        try {
            attendanceApi.alterarStatusProjetoTecnico(idAtendimento, 'APROVADO');
            concluirEtapa('Projeto Técnico');
            setAlertInfo({ severity: 'success', message: 'Projeto aprovado com sucesso!' });
        } catch (error) {
            setAlertInfo({ severity: 'error', message: 'Erro ao aprovar projeto.' });
        }
        setShowAlert(true);
    }    

    function handleInputChangeProducao(e: React.ChangeEvent<HTMLInputElement>) {
        const { name, value } = e.target;
        setProducao((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
    };

    function togglspread(section: keyof typeof expanded) {
        setExpanded((prev) => ({ ...prev, [section]: !prev[section] }));
    }

    async function buscarAtendimento() {
        try {
            const resposta = await attendanceApi.getAttendance(idAtendimento)
            const data = resposta.data
            setAtualizarEstado(data.atualizadoEm)
            setEstado(data.estado)
            setMedicao({
                dataEnvio: data.data_envio_medicao,
                mensagem: data.mensagem_medicao,
                atualizadoEm: data.atualizadoEm_medicao
            })
            setProducao({
                dataEnvio: data.data_envio_producao,
                mensagem: data.mensagem_producao,
                atualizadoEm: data.atualizadoEm_producao
            })
            setProjetoTecnico({
                dataAprovacao: data.dataAprovacaoProjeto ?? '',
                mensagem: data.mensagem_projeto ?? '',
                arquivo: null, // Apenas a referência ao arquivo, a busca é feita separadamente
                atualizadoEm: data.atualizadoEm_projeto
            });
        } catch (err) {
            setAlertInfo({
                severity: 'error',
                message: 'Erro ao buscar atendimento.',
            });
            setShowAlert(true);
        }
    }

    async function concluirEtapa(tipo: string) {
        try {
            if(tipo === 'Medição') {
                await attendanceApi.atualizarEstadoAtendimento(idAtendimento.toString(), 'Projeto Técnico');
            } else if(tipo === 'Projeto Técnico') {
                await attendanceApi.atualizarEstadoAtendimento(idAtendimento.toString(), 'Produção');
            } else if (tipo === 'Produção') {
                await attendanceApi.atualizarEstadoAtendimento(idAtendimento.toString(), 'Montagem');
                clientContext.reloadClientes();
            }
            await buscarAtendimento()
            setAlertInfo({ severity: 'success', message: 'Etapa concluída!' });
            setShowAlert(true);
        } catch (err) {
            setAlertInfo({
                severity: 'error',
                message: 'Erro ao concluir etapa.',
            });
            setShowAlert(true);
        }
    }

    async function enviarMensagemParaCliente(tipo: string) {
        setAlertInfo({ severity: 'success', message: 'Mensagem enviada com sucesso!' });
        setShowAlert(true);
        try {
            if (tipo === 'Medição') {
                if (!validateCamposMedicoes()) {
                    return
                }

                setLoading({ ...loading, medicao: true });
                await attendanceApi.atualizarMensagemAtendimento(idAtendimento.toString(), tipo, medicao.mensagem, new Date(medicao.dataEnvio).toISOString())
            } else if (tipo === 'Projeto Técnico') {
                setLoading({ ...loading, projeto: true });
                await attendanceApi.atualizarMensagemAtendimento(idAtendimento.toString(), tipo, projetoTecnico.mensagem, new Date().toISOString())
            } else {
                if (!validateCamposProducao()) {
                    return
                }

                setLoading({ ...loading, producao: true });
                await attendanceApi.atualizarMensagemAtendimento(idAtendimento.toString(), tipo, producao.mensagem, new Date(producao.dataEnvio).toISOString())
            }

            await delay(1000)
        } catch (error) {
            setAlertInfo({
                severity: 'error',
                message: 'Erro ao enviar mensagem.',
            });
            setShowAlert(true);
        } finally {
            setLoading({ medicao: false, producao: false, projeto: false });
        }
    }

    function validateCamposMedicoes() {
        const newErrors = {
            medicaoData: medicao.dataEnvio === '',
            medicaoMensagem: medicao.mensagem === '',
        };
        setErrorsMedicao((prevErrors) => ({ ...prevErrors, ...newErrors }));

        return !Object.values(newErrors).includes(true);
    }

    function validateCamposProducao() {
        const newErrors = {
            producaoData: producao.dataEnvio === '',
            producaoMensagem: producao.mensagem === '',
        };
        setErrorsProducao((prevErrors) => ({ ...prevErrors, ...newErrors }));

        return !Object.values(newErrors).includes(true);
    }

    useEffect(() => {
        buscarAtendimento()
        buscarArquivoExistente(); // Buscar arquivo técnico salvo
    }, [])


    const isMedicaoProjetoProducao = estado === 'Produção' || estado === 'Projeto Técnico'
    const isMontagemFeedback = estado === 'Montagem' || estado === 'Realizou Montagem' || estado === 'Feedback' || estado === 'Venda Concluída'

    function handleAlertClose() {
        setShowAlert(false)
    }

    return (
        <>
            {expanded.geral ? (
                <>
                    <CustomSnackbar
                        open={showAlert}
                        severity={alertInfo.severity}
                        message={alertInfo.message}
                        handleClose={handleAlertClose}
                    />
                    <>
                        <div className={styles.cabecalho_container}>
                            <div className={`${styles.cabecalho} ${isMontagemFeedback && styles.cabecalho_ok}`}>
                                {isMontagemFeedback ? (
                                    <CheckCircleIcon className={styles.etapa_concluida} />
                                ) : (
                                    <RadioButtonUncheckedIcon
                                    />
                                )}


                                <div className={styles.titulo_info}>
                                    <h4>Produção</h4>

                                    {(atualizarEstado !== '' && (estado === 'Produção' || isMontagemFeedback)) && (
                                        <span>
                                            Útima modificação {formataDataEmDDMMYYYYHM(new Date(atualizarEstado))}
                                        </span>
                                    )}

                                </div>

                            </div>

                            <div className={''}>
                                <IconButton onClick={() => togglspread('geral')}>
                                    <ExpandLessIcon fontSize='large' />
                                </IconButton>
                            </div>
                        </div>

                        <Divider style={{ marginBottom: '1rem' }} />
                    </>

                    <div className={styles.formulario_producao_container}>
                        <div className={`${styles.medicao_container} ${(isMedicaoProjetoProducao || isMontagemFeedback) && styles.estado_medicao_container}`}>
                            <div className={styles.cabecalho}>
                                <div className={styles.titulo}>
                                    {isMedicaoProjetoProducao || isMontagemFeedback ? (<CheckCircleIcon />) : (<RadioButtonUncheckedIcon />)}

                                    <div className={styles.titulo_info}>

                                        <h4>Medição</h4>

                                        {(isMedicaoProjetoProducao || isMontagemFeedback) && (
                                            <span>
                                                Ultima modificação: {formataDataEmDDMMYYYYHM(new Date(medicao.atualizadoEm))}
                                            </span>
                                        )
                                        }
                                    </div>
                                </div>

                                <div className={`${styles.acao_toggle} ${(isMedicaoProjetoProducao || isMontagemFeedback) ? styles.acao_toggle_medicao : ''}`}>
                                    <Button
                                        startIcon={<CheckCircleIcon />}
                                        className={styles.concluir_etapa}
                                        onClick={() => concluirEtapa('Medição')}
                                        disabled={isMedicaoProjetoProducao || isMontagemFeedback}
                                    >
                                        {(isMedicaoProjetoProducao || isMontagemFeedback) ? 'ETAPA CONCLUIDA' : 'CONCLUIR ETAPA'}
                                    </Button>

                                    <div className={''}>
                                        {expanded.medicao ? (
                                            <IconButton onClick={() => togglspread('medicao')}>
                                                <ExpandLessIcon fontSize='large' />
                                            </IconButton>
                                        ) :
                                            <IconButton onClick={() => togglspread('medicao')}>
                                                <ExpandMoreIcon fontSize='large' />
                                            </IconButton>
                                        }
                                    </div>
                                </div>

                            </div>

                            <Divider style={{ marginBottom: '3rem', }} />

                            {expanded.medicao && (
                                <form>
                                    <TextField
                                        id="outlined-basic"
                                        label="Data de agendamento"
                                        variant="outlined"
                                        type="date"
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        name="dataEnvio"
                                        fullWidth
                                        value={medicao.dataEnvio ? new Date(medicao.dataEnvio).toISOString().split("T")[0] : ""}
                                        onChange={handleInputChangeMedicao}
                                        error={errorsMedicao.data}
                                        helperText={errorsMedicao.data ? "Campo obrigatório" : ""}
                                    />

                                    <TextField
                                        name="mensagem"
                                        type='text'
                                        label='Mensagem'
                                        variant='outlined'
                                        multiline
                                        fullWidth
                                        value={medicao.mensagem}
                                        onChange={handleInputChangeMedicao}
                                        error={errorsMedicao.mensagem}
                                        helperText={errorsMedicao.mensagem ? "Campo obrigatório" : ""}
                                        sx={{
                                            '& .MuiInputBase-input': {
                                              maxHeight: '100px',
                                              overflowY: 'auto'
                                            },
                                            '& textarea': {
                                              overflowY: 'auto !important'
                                            }
                                          }}
                                    />

                                    <Button
                                        className={styles.enviar_mensagem}
                                        type='button'
                                        variant='contained'
                                        onClick={() => enviarMensagemParaCliente('Medição')}
                                        disabled={isMedicaoProjetoProducao || isMontagemFeedback}
                                        
                                    >
                                        {loading.medicao ? 'Enviando...' : 'ENVIAR MENSAGEM'}
                                    </Button>
                                </form>
                            )
                            }
                        </div>


                        <div className={`${styles.projeto_tecnico_container} ${(!!projetoTecnico.dataAprovacao) && styles.estado_medicao_container}`}>
                            <div className={styles.cabecalho}>
                                <div className={styles.titulo}>
                                    {!!projetoTecnico.dataAprovacao ? (<CheckCircleIcon />) : (<RadioButtonUncheckedIcon />)}

                                    <div className={styles.titulo_info}>

                                        <h4>Projeto Técnico</h4>

                                        {(isMedicaoProjetoProducao || isMontagemFeedback) && (
                                            <span>
                                                {projetoTecnico.dataAprovacao ? 
                                                `Projeto Aprovado em ${formataDataEmDDMMYYYYHM(new Date(projetoTecnico.dataAprovacao))}` 
                                                : 
                                                `Última modificação: ${formataDataEmDDMMYYYYHM(new Date(projetoTecnico.atualizadoEm))}`
                                                }
                                            </span>
                                        )
                                        }
                                    </div>
                                </div>

                                <div className={`${styles.acao_toggle} ${!!projetoTecnico.dataAprovacao ? styles.acao_toggle_projeto : ''}`}>
                                        <Button
                                            startIcon={<CheckCircleIcon />}
                                            className={styles.concluir_etapa}
                                            onClick={aprovarProjeto}
                                            disabled={estado === 'Produção' || !!projetoTecnico.dataAprovacao}
                                            //disabled={isMedicaoProjetoProducao || isMontagemFeedback}

                                        >
                                            {
                                                (estado === 'Produção' || !!projetoTecnico.dataAprovacao) ? 'Projeto Aprovado' : 'Aprovar Projeto'
                                            }
                                        </Button>

                                        <div className={''}>
                                            {expanded.projeto ? (
                                                <IconButton onClick={() => togglspread('projeto')}>
                                                    <ExpandLessIcon fontSize='large' />
                                                </IconButton>
                                            ) :
                                                <IconButton onClick={() => togglspread('projeto')}>
                                                    <ExpandMoreIcon fontSize='large' />
                                                </IconButton>
                                            }
                                        </div>
                                    </div>
                            </div>

                           

                            <Divider style={{ marginBottom: '1rem' }} />

                            {expanded.projeto && (

                                <form>
                                    <TextField
                                        label="Mensagem"
                                        variant="outlined"
                                        multiline
                                        fullWidth
                                        name="mensagem"
                                        value={projetoTecnico.mensagem}
                                        onChange={handleInputChangeProjeto}
                                        sx={{
                                            '& .MuiInputBase-input': {
                                              maxHeight: '100px',
                                              overflowY: 'auto'
                                            },
                                            '& textarea': {
                                              overflowY: 'auto !important'
                                            }
                                          }}
                                    />

                                    {/* Input oculto */}
                                    <input
                                        type="file"
                                        accept="application/pdf"
                                        ref={fileInputRef}
                                        onChange={handleFileUpload}
                                        style={{ display: "none" }}
                                    />

                                    <div className={styles.section_enviar_arquivo}>
                                        <span className={styles.span_arquivo_enviado}>
                                            {projetoTecnico.arquivo ? (
                                                <>Arquivo selecionado: <strong>{projetoTecnico.arquivo.name}</strong></>
                                            ) : (
                                                arquivoSalvo ? (
                                                    <>Arquivo já enviado: <strong>{arquivoSalvo}</strong></>
                                                ) : (
                                                    "Nenhum arquivo enviado."
                                                )
                                            )}
                                            
                                        </span>

                                        <Button
                                            className={styles.buscar_arquivo}
                                            variant="contained"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={!!projetoTecnico.dataAprovacao}
                                        >
                                            {arquivoSalvo ? "Alterar arquivo" : "Buscar arquivo"}
                                        </Button>
                                    </div>

                                    <Button 
                                        className={styles.enviar_mensagem} 
                                        variant="contained" 
                                        onClick={uploadProjeto} 
                                        style={{ marginLeft: "10px" }}
                                        disabled={!!projetoTecnico.dataAprovacao}
                                    >
                                        {loadingProjeto ? <CircularProgress size={24} sx={{ color: 'white' }} /> : <></>}
                                        Enviar Projeto
                                    </Button>

                                    
                                </form>
                            )}
                        </div>



                        <div className={`${styles.producao_container} ${(isMontagemFeedback) && styles.estado_entrega_container}`}>
                            <div className={styles.cabecalho}>
                                <div className={`${styles.titulo} ${(isMontagemFeedback) && styles.titulo_ok}`}>
                                    {(isMontagemFeedback) ? (<CheckCircleIcon />) : (<RadioButtonUncheckedIcon />)}

                                    <div className={styles.titulo_info}>
                                        <h4>Produção/Entrega</h4>

                                        {(isMontagemFeedback) && (
                                            <span>Ultima modificação {formataDataEmDDMMYYYYHM(new Date(producao.atualizadoEm))}</span>
                                        )}
                                    </div>

                                </div>

                                <div className={`${styles.acao_toggle} ${(isMontagemFeedback) ? styles.acao_toggle_entrega : ''}`}>
                                    <Button
                                        startIcon={<CheckCircleIcon />}
                                        className={styles.concluir_etapa}
                                        onClick={() => concluirEtapa('Produção')}
                                        disabled={isMontagemFeedback}
                                    >
                                        {
                                            (isMontagemFeedback) ? 'ETAPA CONCLUIDA' : 'CONCLUIR ETAPA'
                                        }
                                    </Button>
                                    <div className={''}>
                                        {
                                            expanded.producao ? (
                                                <IconButton onClick={() => togglspread('producao')}>
                                                    <ExpandLessIcon fontSize='large' />
                                                </IconButton>
                                            ) :
                                                <IconButton onClick={() => togglspread('producao')}>
                                                    <ExpandMoreIcon fontSize='large' />
                                                </IconButton>
                                        }
                                    </div>
                                </div>

                            </div>

                            <Divider style={{ marginBottom: '3rem', }} />

                            {expanded.producao && (
                                <form>
                                    <TextField
                                        id="outlined-basic"
                                        label="Data de previsao pra finalização"
                                        variant="outlined"
                                        type="date"
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        name="dataEnvio"
                                        fullWidth
                                        value={producao.dataEnvio ? new Date(producao.dataEnvio).toISOString().split("T")[0] : ""}
                                        onChange={handleInputChangeProducao}
                                        error={errorsProducao.data}
                                        helperText={errorsProducao.data ? "Campo obrigatório" : ""}
                                        
                                    />

                                    <TextField
                                        type='text'
                                        label='Mensagem'
                                        variant='outlined'
                                        multiline
                                        fullWidth
                                        value={producao.mensagem}
                                        onChange={handleInputChangeProducao}
                                        name='mensagem'
                                        error={errorsProducao.mensagem}
                                        helperText={errorsProducao.mensagem ? "Campo obrigatório" : ""}
                                        sx={{
                                            '& .MuiInputBase-input': {
                                              maxHeight: '100px',
                                              overflowY: 'auto'
                                            },
                                            '& textarea': {
                                              overflowY: 'auto !important'
                                            }
                                          }}
                                    />

                                    <Button
                                        className={styles.enviar_mensagem}
                                        type='button'
                                        variant='contained'
                                        onClick={() => enviarMensagemParaCliente('Produção')}
                                        disabled={isMontagemFeedback}
                                    >
                                        {loading.producao ? 'Enviando...' : 'ENVIAR MENSAGEM'}
                                    </Button>
                                </form>
                            )
                            }
                        </div>
                    </div>
                </>
            )
                : (
                    <>
                        <div className={styles.cabecalho_container}>
                            <div className={styles.cabecalho}>
                                {isMontagemFeedback ? (
                                    <CheckCircleIcon className={styles.etapa_concluida} />
                                ) : (
                                    <RadioButtonUncheckedIcon
                                    />
                                )}

                                <div className={styles.titulo_info}>
                                    <h4>Produção</h4>

                                    {(atualizarEstado !== '' && isMontagemFeedback) && (
                                        <span>
                                            Útima modificação {formataDataEmDDMMYYYYHM(new Date(atualizarEstado))}
                                        </span>
                                    )}

                                </div>
                            </div>

                            <div className={''}>
                                {isMontagemFeedback && (
                                    <span className={styles.processo_concluido}>Processo concluído</span>
                                )}
                                <IconButton onClick={() => togglspread('geral')}>
                                    <ExpandMoreIcon fontSize='large' />
                                </IconButton>
                            </div>
                        </div>

                        <Divider style={{ marginTop: '1rem' }} />
                    </>
                )
            }
        </>
    )
}